import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const POLL_MS = 2000;

/** Dispara el push de stock → Tienda Nube (corre en background) y sigue el progreso. */
export function useSyncStockTN({ onDone } = {}) {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const timer = useRef(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const stop = () => { if (timer.current) { clearInterval(timer.current); timer.current = null; } };

  const finish = useCallback((st) => {
    stop();
    setSyncing(false);
    setStatus(st);
    if (st?.error) {
      toast.error(`Error al sincronizar stock con TN: ${st.error}`);
    } else if (st?.result) {
      const r = st.result;
      toast.success(
        `Stock TN sincronizado · ${r.sincronizados} actualizados · ${r.sin_cambio ?? 0} sin cambio · ${r.saltados} sin match · ${r.fallidos} fallos`
      );
    }
    onDoneRef.current?.(st);
  }, []);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/tiendanube/sync-stock/status");
      setStatus(data);
      if (!data.running) finish(data);
    } catch (e) {
      stop();
      setSyncing(false);
      toast.error("No se pudo consultar el estado de la sincronización");
    }
  }, [finish]);

  const start = useCallback(async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/tiendanube/sync-stock");
      setStatus(data);
      if (!data.started) toast.info("Ya hay una sincronización en curso, mostrando su progreso…");
      stop();
      timer.current = setInterval(poll, POLL_MS);
    } catch (e) {
      setSyncing(false);
      toast.error(e?.response?.data?.detail || "Error al sincronizar stock con TN");
    }
  }, [poll]);

  // Si al montar ya hay una corrida en curso (otro usuario / auto-sync), la seguimos.
  useEffect(() => {
    let alive = true;
    api.get("/tiendanube/sync-stock/status").then(({ data }) => {
      if (!alive || !data?.running) return;
      setSyncing(true);
      setStatus(data);
      timer.current = setInterval(poll, POLL_MS);
    }).catch(() => {});
    return () => { alive = false; stop(); };
  }, [poll]);

  const progressLabel = syncing
    ? status?.fase === "empujando" && status?.total
      ? `Sincronizando ${status.done}/${status.total}…`
      : "Sincronizando…"
    : null;

  return { syncing, status, start, progressLabel };
}
